import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { PaymentSuccess } from './PaymentSuccess';
import { Step3 } from './Step3';
import { Step4 } from './Step4';
import { Complete } from './Complete';
import { CalendlyBooking } from './CalendlyBooking';
import { saveStepData, loadStepData, clearStepData } from '@/utils/localStorage';
import { ContactUpdatePayload, ContactUpdateResponse } from '@shared/types/contactUpdateTypes';
import { Snackbar, useSnackbar } from '@app/components/shared/Snackbar';

export function OneOffFlow() {
  const [stepData, setStepData] = useState<Record<string, any>>({});
  const navigate = useNavigate();
  const { step = '1' } = useParams();
  const currentStep = parseInt(step);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  // Load saved data on mount
  useEffect(() => {
    const savedData = loadStepData('one-off');
    if (Object.keys(savedData).length > 0) {
      setStepData(savedData);
      console.log('Restored one-off flow data from localStorage:', savedData);
    }
  }, []);

  // Save data to localStorage whenever stepData changes
  useEffect(() => {
    if (Object.keys(stepData).length > 0) {
      saveStepData('one-off', stepData);
    }
  }, [stepData]);

  const handleStep1Next = (data: any) => {
    setStepData(prev => ({ ...prev, step1: data }));
    navigate('/one-off/2');
  };

  const handleStep2Next = (data: any) => {
    setStepData(prev => ({ ...prev, step2: data }));
    console.log('Complete flow data:', { ...stepData, step2: data });
    navigate('/one-off/success');
  };

  const handlePaymentNext = () => {
    navigate('/one-off/3');
  };

  const handleStep3Next = (data: any) => {
    setStepData(prev => ({ ...prev, step3: data }));
    
    // Route based on selected option
    if (data.setupMethod === 'call') {
      navigate('/one-off/booking');
    } else {
      navigate('/one-off/4');
    }
  };

  const handleBookingComplete = (bookingData: any) => {
    const completeData = { ...stepData, booking: bookingData };
    setStepData(completeData);
    
    // Clear localStorage since flow is complete (call booked)
    clearStepData('one-off');
    
    navigate('/one-off/call-booked');
  };

  const handleStep4Next = async (data: any) => {
    const completeData: Record<string, any> = { ...stepData, step4: data };
    setStepData(completeData);
    console.log('Complete one-off flow data:', completeData);
    
    try {
      // Validate we have the necessary data before making API call
      if (!completeData.step2?.email) {
        console.warn('⚠️ No customer email found in step2 data, skipping contact update');
      } else if (!data.recipientDetails || data.recipientDetails.length === 0) {
        console.warn('⚠️ No recipient details to update, skipping contact update');
      } else {
        // Update contact with detailed Step4 information
        const payload: ContactUpdatePayload = {
          customerInfo: completeData.step2, // Customer info from Step2
          recipientDetails: data.recipientDetails, // Detailed recipient info from Step4
          eventBudgets: data.eventBudgets, // Event budgets (mostly for subscription)
          flowType: 'one-off'
        };
        
        const response = await fetch('/api/customer/update-details', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json() as ContactUpdateResponse;
        
        if (!response.ok || !result.success) {
          // Try to get the actual error message from the server response
          const errorMessage = result.error || `HTTP error! status: ${response.status}`;
          throw new Error(errorMessage);
        }
        
        console.log('✅ Contact details updated successfully');
      }
    } catch (error) {
      console.error('❌ Error updating contact details:', error);
      // Show error to user and don't proceed
      showSnackbar(error instanceof Error ? error.message : 'Failed to save contact details. Please try again.', 'error');
      return; // Don't continue to completion page
    }
    
    // Clear localStorage since flow is complete
    clearStepData('one-off');
    
    navigate('/one-off/complete');
  };

  const handleBack = (currentStepData?: any) => {
    // Save current step data when going back
    if (currentStepData) {
      if (currentStep === 2) {
        setStepData(prev => ({ ...prev, step2: currentStepData }));
      } else if (currentStep === 3) {
        setStepData(prev => ({ ...prev, step3: currentStepData }));
      } else if (currentStep === 4) {
        setStepData(prev => ({ ...prev, step4: currentStepData }));
      }
    }

    if (currentStep === 1) {
      navigate('/');
    } else if (currentStep === 3) {
      navigate('/one-off/success');
    } else if (currentStep === 4) {
      navigate('/one-off/3');
    } else {
      navigate(`/one-off/${currentStep - 1}`);
    }
  };

  const navigateToStep = (step: number) => {
    navigate(`/one-off/${step}`);
  };

  if (step === 'booking') {
    return (
      <CalendlyBooking 
        onBookingComplete={handleBookingComplete}
        stepData={stepData}
        flowType="one-off"
      />
    );
  }

  if (step === 'call-booked') {
    return (
      <Complete 
        bookingData={stepData.booking}
        stepData={stepData}
        flowType="one-off"
        variant="call-booked"
      />
    );
  }

  if (step === 'success') {
    return (
      <PaymentSuccess 
        stepData={stepData}
        flowType="one-off"
        onNext={handlePaymentNext}
      />
    );
  }

  if (step === 'complete') {
    return (
      <Complete 
        stepData={stepData}
        flowType="one-off"
        variant="form-complete"
      />
    );
  }

  if (currentStep === 2) {
    return (
      <Step2 
        onNext={handleStep2Next} 
        onBack={handleBack}
        onStepClick={navigateToStep}
        stepData={stepData}
        flowType="one-off"
      />
    );
  }

  if (currentStep === 3) {
    return (
      <Step3 
        onNext={handleStep3Next} 
        onBack={handleBack}
        onStepClick={navigateToStep}
        stepData={stepData}
        flowType="one-off"
      />
    );
  }

  if (currentStep === 4) {
    return (
      <>
        <Step4 
          onNext={handleStep4Next} 
          onBack={handleBack}
          onStepClick={navigateToStep}
          stepData={stepData}
          flowType="one-off"
        />
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          isVisible={snackbar.isVisible}
          onClose={hideSnackbar}
        />
      </>
    );
  }

  return (
    <Step1 
      onNext={handleStep1Next} 
      flowType="one-off"
      stepData={stepData}
    />
  );
}